// Web implementation of stale code-split chunk recovery.
//
// After a frontend redeploy the chunk filename hashes rotate. A browser holding
// an older index.html (especially iPad / Mobile Safari resuming a backgrounded
// tab) requests a chunk hash that no longer exists. Cloudflare Pages serves the
// SPA index.html (200) for that missing asset, so the dynamic import either
// rejects ("Loading chunk N failed") or throws a SyntaxError parsing HTML as JS
// ("Unexpected token '<'"). Either way the lazily loaded view crashes.
//
// The fix is to reload once so the browser fetches a fresh index.html (made
// no-cache in WS1 / DHT-4109) that references the current chunk hashes. A
// timestamped guard in sessionStorage prevents a reload loop if the failure
// somehow persists. The same guard key is shared with the webpack
// RetryChunkLoadPlugin lastResortScript so the two mechanisms never double-fire.

const RELOAD_TS_KEY = "dapple-chunk-reload-ts";
const RELOAD_WINDOW_MS = 30_000;

const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk\b|Loading CSS chunk|error loading dynamically imported module|Importing a module script failed|module script failed|Unexpected token '<'|expected expression, got '<'/i;

export function isChunkError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === "string") return CHUNK_ERROR_PATTERN.test(error);
  const e = error as { name?: string; message?: string };
  return CHUNK_ERROR_PATTERN.test(`${e.name ?? ""} ${e.message ?? ""}`);
}

// Reloads the page at most once per RELOAD_WINDOW_MS. Returns true if a reload
// was triggered. Safe to call from multiple places (error boundary, global
// listeners, webpack lastResortScript) — the shared timestamp coordinates them.
export function reloadOnceForChunkError(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY) ?? "0");
    if (now - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(now));
    window.location.reload();
    return true;
  } catch {
    // sessionStorage may be unavailable (private mode / blocked storage). The
    // loop guard is then best-effort, but recovering the page still beats
    // leaving the user on a broken screen.
    try {
      window.location.reload();
      return true;
    } catch {
      // Nothing else we can do; report that no reload was triggered.
      return false;
    }
  }
}

// Global backstop for chunk failures that are not caught by a React error
// boundary — e.g. the top-level dynamic import in the app entry, or a rejected
// import() in code outside the forms flow.
export function installChunkErrorReloadHandler(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", event => {
    if (isChunkError(event.error) || isChunkError(event.message)) {
      reloadOnceForChunkError();
    }
  });
  window.addEventListener("unhandledrejection", event => {
    if (isChunkError(event.reason)) {
      reloadOnceForChunkError();
    }
  });
}
